import { useSyncExternalStore } from "react"

import { DEV_ID_STORAGE_KEY } from "@/constants/storage-keys"

/**
 * The dev_id every surface that can hand work to a local worker shares.
 *
 * One raw string under one key, backed by a single store rather than per-component
 * state: the control that edits it is app-wide, so a component mounted before the
 * value changed would otherwise keep launching against a stale one while the UI
 * showed the new value.
 *
 * Values are trimmed, since surrounding space is invisible in the field but would be
 * sent as part of the lane name and match no worker. Clearing removes the key instead
 * of storing an empty string, which would read back as a set-but-empty lane.
 *
 * Not `useLocalStorage`: that one JSON-encodes, and this key holds a bare string that
 * predates it.
 */

const listeners = new Set<() => void>()

let snapshot: string | null = null

function readStoredDevId(): string {
  if (typeof window === "undefined") {
    return ""
  }
  return localStorage.getItem(DEV_ID_STORAGE_KEY)?.trim() ?? ""
}

function getSnapshot(): string {
  if (snapshot === null) {
    snapshot = readStoredDevId()
  }
  return snapshot
}

function getServerSnapshot(): string {
  return ""
}

function emit(): void {
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)

  // A write in another tab does not fire in this one, so re-read on its storage event.
  const handleStorage = (event: StorageEvent) => {
    if (event.key === DEV_ID_STORAGE_KEY || event.key === null) {
      snapshot = readStoredDevId()
      emit()
    }
  }
  window.addEventListener("storage", handleStorage)

  return () => {
    listeners.delete(listener)
    window.removeEventListener("storage", handleStorage)
  }
}

/**
 * Store a dev_id, or clear it when blank.
 *
 * @param devId The value typed into a dev_id field.
 */
export function setDevId(devId: string): void {
  const trimmedDevId = devId.trim()
  if (trimmedDevId === getSnapshot()) {
    return
  }

  snapshot = trimmedDevId
  if (trimmedDevId) {
    localStorage.setItem(DEV_ID_STORAGE_KEY, trimmedDevId)
  } else {
    localStorage.removeItem(DEV_ID_STORAGE_KEY)
  }
  emit()
}

/**
 * Read the shared dev_id, re-rendering when any surface changes it.
 */
export function useDevId(): string {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/**
 * Reset the in-process cache. Tests only, so a stubbed localStorage is re-read.
 */
export function resetDevIdCacheForTests(): void {
  snapshot = null
}
